<div
  data-testid="compose-modal"
  class="compose"
  (drop)="handleDropFiles($event)"
  (dragenter)="dragOver = dragOver + 1"
  (dragleave)="dragOver = dragOver - 1"
  [class.drag-over]="dragOver > 0"
  [class.dense]="dense"
  #droppableArea
>
  @if (draftOrigin(); as origin) {
    <div class="px-8 py-4 text-sm text-gray-700" data-testid="compose-source-origin" role="status">
      <p>{{ origin.label }}</p>
      @if (originStatus()) {
        <p>{{ originStatus() }}</p>
      }
    </div>
  }
  @if (sendAsVisible()) {
    <div class="row send-as">
      <div class="send-as-label">Send as:</div>
      @if (sendAsLocked()) {
        <!--
          The message already lives in a mailbox — an existing draft, or a reply/forward answering a parent
          that lives in one tenant. Shown, so the sender is never a mystery, but not offered: changing it
          would have to move the row between mailboxes.
        -->
        <div class="send-as-fixed" data-testid="send-as-fixed">
          <span class="send-as-name">{{ sendAsSelected()?.name }}</span>
          <span class="send-as-address">{{ sendAsSelected()?.address }}</span>
          @if (sendAsLockedByUpload()) {
            <!--
              The file is already in this mailbox's storage — an attachment or an image in the body,
              which is why this does not say "attachment". Said out loud rather than left as a control
              that quietly stops responding.
            -->
            <span class="send-as-fixed-reason" data-testid="send-as-fixed-reason">fixed by the uploaded file</span>
          }
        </div>
      } @else {
        <div class="send-as-options" role="radiogroup" aria-label="Send as">
          @for (identity of sendAsIdentities(); track identity.tenantId) {
            <button
              type="button"
              role="radio"
              class="send-as-option"
              [class.selected]="identity.tenantId === sendAsSelected()?.tenantId"
              [attr.aria-checked]="identity.tenantId === sendAsSelected()?.tenantId"
              (click)="selectSendAs(identity)"
            >
              <span class="send-as-name">{{ identity.name }}</span>
              <span class="send-as-address">{{ identity.address }}</span>
            </button>
          }
        </div>
      }
    </div>
  }

  <div class="row">
    <div class="row-rest">
      <app-autocomplete-multiple-input
        appearance="sapphire"
        size="s"
        [noBorder]="true"
        title="To:"
        class="recipients"
        placeholder="Type to choose contact"
        [suggestions]="contactsSuggestions"
        valueKey="address"
        [inputFormControl]="form.controls.to"
        [itemTemplate]="contactTemplate"
        [containerStyles]="recipientContainerStyles"
        [collapsible]="true"
        [removeLastOnBackspace]="true"
        [useInputText]="true"
        [useInputTextGetValue]="useInputTextGetValue"
        [useInputTextSpaceCommits]="recipientCommitsOnSpace"
        [optionsDraggable]="true"
        appDroppable
        appDroppableHoverClass="drag-over"
        [appDroppablePredicate]="dndPredicate(form.controls.to.value)"
        (appDroppableDrop)="handleDrop($event, 'to')"
      />
    </div>
    @if (!bcc) {
      <div class="row-more" (click)="bcc = true" appTooltip="Add Bcc recipients">Bcc</div>
    }
  </div>

  <div class="row">
    <div class="row-rest">
      <app-autocomplete-multiple-input
        appearance="sapphire"
        size="s"
        [noBorder]="true"
        title="CC:"
        class="recipients"
        placeholder="Type to choose contact"
        [suggestions]="contactsSuggestions"
        valueKey="address"
        [inputFormControl]="form.controls.cc"
        [itemTemplate]="contactTemplate"
        [containerStyles]="recipientContainerStyles"
        [collapsible]="true"
        [removeLastOnBackspace]="true"
        [useInputText]="true"
        [useInputTextGetValue]="useInputTextGetValue"
        [useInputTextSpaceCommits]="recipientCommitsOnSpace"
        [optionsDraggable]="true"
        appDroppable
        appDroppableHoverClass="drag-over"
        (appDroppableDrop)="handleDrop($event, 'cc')"
        [appDroppablePredicate]="dndPredicate(form.controls.cc.value)"
      />
    </div>
  </div>

  @if (bcc) {
    <div class="row">
      <div class="row-rest">
        <app-autocomplete-multiple-input
          appearance="sapphire"
          size="s"
          [noBorder]="true"
          title="BCC:"
          class="recipients"
          placeholder="Type to choose contact"
          [suggestions]="contactsSuggestions"
          valueKey="address"
          [inputFormControl]="form.controls.bcc"
          [itemTemplate]="contactTemplate"
          [containerStyles]="recipientContainerStyles"
          [collapsible]="true"
          [removeLastOnBackspace]="true"
          [useInputText]="true"
          [useInputTextGetValue]="useInputTextGetValue"
          [useInputTextSpaceCommits]="recipientCommitsOnSpace"
          [optionsDraggable]="true"
          appDroppable
          appDroppableHoverClass="drag-over"
          (appDroppableDrop)="handleDrop($event, 'bcc')"
          [appDroppablePredicate]="dndPredicate(form.controls.bcc.value)"
        />
      </div>
    </div>
  }

  @if (overRecipientCap()) {
    <div class="row no-border">
      <div class="text-error text-sm">{{ recipientCapMessage }}</div>
    </div>
  }

  <div class="row subject-row">
    <div class="row-title row-title-subject">Subject:</div>
    <div class="row-rest">
      <input class="row-subject" [formControl]="form.controls.title" />
    </div>
  </div>

  <div class="card">
    <div class="body">
      <app-rich-text-editor
        [inline]="true"
        [preserveAbsoluteUrls]="true"
        [toolbarContainerSelector]="'#' + toolbarContainerId"
        [control]="form.controls.bodyHtml"
        [editorCommandExecutor]="editorCommandExecutor"
        [disableTableMenu]="true"
        [showToolbar]="showToolbar"
        [uploadRequestContext]="inlineUploadRequestContext"
        [inlineImageRefusal]="inlineImageRefusal"
        [focusBodyOnInit]="focusBodyOnInit"
        (imageUploaded)="handleEditorUploadedImage($event)"
        (imageUploadFailed)="handleEditorImageUploadFailed($event)"
        (editorReady)="onEditorReady()"
      />
    </div>

    <!-- Pinned below the scrolling body so a newly added attachment is always visible,
         no matter how long the message or the quoted thread is. -->
    <app-compose-attachments
      class="attachments"
      [items]="composeAttachments"
      [warningLimit]="attachmentsWarningLimit"
      (remove)="removeAttachment($event)"
      (browse)="attachFile()"
    />

    @if (showStitchFilesDropZone()) {
      <div
        class="stitch-files-dropzone"
        appDroppable
        appDroppableHoverClass="drag-over"
        [appDroppablePredicate]="stitchFileDropPredicate"
        (appDroppableDrop)="handleStitchFileDrop($event)"
      >
        <span class="stitch-files-dropzone-title">Drag files from Stitch.ai to attach</span>
      </div>
    }

    @if (canShowOutboundKnots | async) {
      <app-outbound-knots-control
        [inputFormControl]="form.controls.sendWithKnots"
        [messageId]="outboundKnotsMessageId"
        (previewRequested)="requestKnotPreview()"
      />
    } @else if (dense) {
      <app-outbound-knots-control [inputFormControl]="unavailableKnotsControl" />
    }

    <div [id]="toolbarContainerId" class="format-toolbar" [class.format-toolbar--hidden]="!showToolbar"></div>

    <div class="footer">
      <app-button2
        class="footer-button toolbar-icon"
        [class.active]="showToolbar"
        appearance="secondary-ghost"
        size="s"
        appTooltip="Format text"
        (click)="showToolbar = !showToolbar"
      >
        <app-icon name="sapphire-text-editor" />
      </app-button2>

      <app-button2
        class="footer-button toolbar-icon"
        appearance="secondary-ghost"
        size="s"
        appTooltip="Add attachment"
        stchPopover
        [stchPopoverTemplate]="attachMenuTemplate"
        stchPopoverAppearance="sapphire-context-menu"
        [stchPopoverCloseOnOutsideClick]="true"
        [stchPopoverShowUntil]="customPopoverClose"
        stchPopoverPlacement="top"
        stchPopoverTrigger="click"
      >
        <app-icon name="sapphire-attachment" />
      </app-button2>

      <app-button2
        class="footer-button toolbar-icon"
        appearance="secondary-ghost"
        size="s"
        appTooltip="Insert image"
        (click)="insertImage()"
      >
        <app-icon name="sapphire-image" />
      </app-button2>

      <app-button2
        class="footer-button toolbar-icon"
        appearance="secondary-ghost"
        size="s"
        appTooltip="Insert signature"
        stchPopover
        [stchPopoverContent]="signaturesTemplate"
        [stchPopoverShowUntil]="customPopoverClose"
        stchPopoverPlacement="top"
        stchPopoverTrigger="click"
      >
        <app-icon name="sapphire-signature" />
      </app-button2>

      <app-button2
        class="footer-button toolbar-icon"
        appearance="secondary-ghost"
        size="s"
        appTooltip="Insert template"
        stchPopover
        [stchPopoverContent]="templatesTemplate"
        [stchPopoverShowUntil]="customPopoverClose"
        stchPopoverPlacement="top"
        stchPopoverTrigger="click"
      >
        <app-icon name="sapphire-template" />
      </app-button2>

      <div class="footer-divider"></div>

      @if (showSaveButton()) {
        <app-button2
          class="footer-button"
          data-testid="compose-save-draft"
          appearance="secondary"
          size="s"
          [disabled]="busy() || filesUploading || !form.valid || originPersistenceBlocked()"
          (click)="saveAndClose()"
          >Save draft</app-button2
        >
      }

      @if (scheduledAtValue()) {
        <app-button2 class="mr-6" appearance="primary-text" (click)="clearScheduledAt()">
          Cancel send at {{ scheduledAtValue() | formatSendLater }}
        </app-button2>
      }

      <app-button2
        class="footer-button"
        data-testid="compose-send"
        [disabled]="busy() || filesUploading || !form.valid || overRecipientCap() || originPersistenceBlocked()"
        appearance="primary"
        size="s"
        (click)="send()"
      >
        {{ sending() ? 'Sending…' : sendButtonText() }}
      </app-button2>

      @if (!scheduledAtValue()) {
        <app-button2
          class="footer-button"
          appearance="primary"
          size="s"
          appTooltip="Schedule"
          stchPopover
          [stchPopoverContent]="sendLaterPopoverTemplate"
          stchPopoverPlacement="top"
          stchPopoverTrigger="click"
          [stchPopoverShowUntil]="customPopoverClose"
          [stchPopoverCustomStyles]="{ overflow: 'visible' }"
        >
          <app-icon name="sapphire-scheduled" class="h-[17px]! items-start!" />
        </app-button2>
      }
    </div>
  </div>

  <div class="droppable-area"></div>

  <input #fileAttachInput type="file" multiple hidden [id]="fileInputId" (change)="handleAttachFilesPicked($event)" />
</div>

<ng-template #contactTemplate let-contact="item">
  <div class="contact">
    <app-user-avatar
      class="contact-avatar-icon"
      [size]="28"
      [contact]="{ name: contact.name, email: contact.address }"
      [usePopover]="false"
    />

    <div class="contact-body">
      <div class="contact-body-heading">
        <div class="contact-body-heading-title">
          {{ contact.name || 'No Name' }}
        </div>
      </div>

      <div class="contact-body-date">
        <div class="contact-body-date-item">
          {{ contact.address || 'No Email' }}
        </div>
      </div>
    </div>
  </div>
</ng-template>

<ng-template #sendLaterPopoverTemplate>
  <app-send-after-context-menu [inputFormControl]="form.controls.scheduledAt" (onSelect)="customPopoverClose.next()" />
</ng-template>

<ng-template #attachMenuTemplate let-close="close">
  <div class="flex flex-col gap-1">
    <!--
      Native <label> (not a JS .click()) so the file dialog keeps its user activation:
      triggering the hidden input programmatically from inside the popover overlay is
      blocked by the browser ("File chooser dialog can only be shown with a user activation").
    -->
    <label
      [attr.for]="fileInputId"
      class="flex w-full items-center gap-4 rounded px-4 py-2 text-base font-normal text-text cursor-pointer hover:bg-gray-100 hover:text-blue-600 focus:bg-gray-100 focus:text-blue-600"
    >
      <span class="flex-1 truncate text-left">Upload file from device</span>
    </label>
    <button
      type="button"
      class="flex w-full items-center gap-4 rounded px-4 py-2 text-base font-normal text-text cursor-pointer hover:bg-gray-100 hover:text-blue-600 focus:bg-gray-100 focus:text-blue-600"
      (click)="insertFileFromStitch(); close.next()"
    >
      <span class="flex-1 truncate text-left">Insert file from Stitch.ai</span>
    </button>
  </div>
</ng-template>

<!-- The signature and template menus sit in the standard menu card (they were bare rows: small, with a scrollbar;
     Johanna, 2026-10-21). -->
<ng-template #signaturesTemplate>
  <app-context-menu-window>
    @if (!signatures?.length) {
      <app-context-menu-item title="New signature" (execute)="handleNewTemplate('signatures')" />
    }

    @for (signature of signatures; track signature.id) {
      <app-context-menu-item [title]="signature.title" (execute)="selectSignature(signature)" />
    }
  </app-context-menu-window>
</ng-template>

<ng-template #templatesTemplate>
  <app-context-menu-window>
    @if (!templates?.length) {
      <app-context-menu-item title="New template" (execute)="handleNewTemplate('templates')" />
    }

    @for (template of templates; track template.id) {
      <app-context-menu-item [title]="template.title" (execute)="selectTemplate(template)" />
    }
  </app-context-menu-window>
</ng-template>
